import { useState } from 'react'
import { api } from '@/lib/api'
import { apiEtapa6 } from '../api'
import Aviso from '../../components/Aviso'
import { competenciaAtual, moeda, pct } from '../../components/format'
import { Cards, dataBR } from '../../etapa2/componentes'

const NOMES_ENCARGOS = {
  inss_patronal: 'INSS patronal', rat: 'RAT', terceiros: 'Terceiros', fgts: 'FGTS',
  pis_folha: 'PIS s/ folha', adicional_aposentadoria_especial: 'Adic. apos. especial',
}
const novoFuncionario = (n) => ({ funcionario_id: `F${n}`, nome: '', salario_base: '', jornada_mensal: '', dependentes_irrf: 0, horas_extras: [], outros_proventos: [], horas_noturnas: [], intervalo_suprimido: [] })

function limparEntrada(competencia, empresaId, funcionarios, feriados, dataPagamento) {
  return {
    empresa_id: empresaId,
    competencia,
    data_pagamento: dataPagamento || null,
    feriados_adicionais: feriados.split(',').map((s) => s.trim()).filter(Boolean),
    funcionarios: funcionarios.map((f) => ({
      ...f,
      jornada_mensal: f.jornada_mensal || null,
      dependentes_irrf: Number(f.dependentes_irrf) || 0,
      horas_extras: f.horas_extras.filter((h) => h.horas !== '' && h.adicional_percentual !== '').map((h) => ({ ...h, data: h.data || null })),
      outros_proventos: f.outros_proventos.filter((o) => o.valor !== ''),
      horas_noturnas: (f.horas_noturnas || []).map((h) => ({ ...h, data: h.data || null })),
      intervalo_suprimido: (f.intervalo_suprimido || []).map((h) => ({ ...h, data: h.data || null })),
    })),
  }
}

export default function FolhaMensal({ empresaId }) {
  const [competencia, setCompetencia] = useState(competenciaAtual())
  const [feriados, setFeriados] = useState('')
  const [dataPagamento, setDataPagamento] = useState('')
  const [funcionarios, setFuncionarios] = useState([novoFuncionario(1)])
  const [resultado, setResultado] = useState(null)
  const [msg, setMsg] = useState({ erro: '', ok: '' })
  const [carregando, setCarregando] = useState(false)

  const [doPonto, setDoPonto] = useState(null)

  // Traz do Ponto e Escala (mês fechado) só as QUANTIDADES: horas extras por percentual e por período de jornada,
  // horas noturnas reduzidas e intervalo suprimido. O valor em dinheiro é calculado aqui na Folha, pelo divisor
  // oficial do funcionário em cada data. Cada colaborador do Ponto é ligado a um funcionário da Folha no cadastro do Ponto.
  async function importarDoPonto() {
    setMsg({ erro: '', ok: '' })
    try {
      const { data } = await api.get(`/ponto/folha/${competencia}`)
      const fs = [...funcionarios.filter((f) => f.nome || f.salario_base)]
      for (const p of data.funcionarios) {
        let i = fs.findIndex((f) => f.funcionario_id === p.funcionario_id)
        if (i < 0) { fs.push({ ...novoFuncionario(fs.length + 1), funcionario_id: p.funcionario_id, nome: p.nome || '' }); i = fs.length - 1 }
        const f = fs[i]
        fs[i] = {
          ...f,
          horas_extras: [...f.horas_extras.filter((h) => !h.origem), ...p.horas_extras.map((h) => ({ ...h, origem: p.origem }))],
          horas_noturnas: p.horas_noturnas.map((h) => ({ ...h, origem: p.origem })),
          intervalo_suprimido: p.intervalo_suprimido.map((h) => ({ ...h, origem: p.origem })),
        }
      }
      setFuncionarios(fs.length ? fs : [novoFuncionario(1)])
      setDoPonto(data)
      const semSalario = fs.filter((f) => f.salario_base === '').length
      setMsg({
        erro: data.nao_conciliados.length ? `${data.nao_conciliados.length} colaborador(es) do Ponto sem ligação com um funcionário da Folha não foram importados: ${data.nao_conciliados.map((x) => x.nome).join(', ')}. Ligue no Ponto (Cadastros → Colaboradores → Funcionário na Folha).` : '',
        ok: `Horas de ${data.funcionarios.length} funcionário(s) trazidas do Ponto (mês fechado).${semSalario ? ` Preencha o salário de ${semSalario} funcionário(s).` : ''}`,
      })
    } catch (e) {
      setMsg({ erro: e.response?.data?.detail || 'Não foi possível ler o Ponto e Escala deste mês.', ok: '' })
    }
  }

  const alterar = (i, campo, valor) => setFuncionarios((fs) => fs.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)))
  const alterarItem = (i, lista, k, campo, valor) =>
    alterar(i, lista, funcionarios[i][lista].map((x, j) => (j === k ? { ...x, [campo]: valor } : x)))

  async function executar(acao) {
    setMsg({ erro: '', ok: '' })
    setCarregando(true)
    const entrada = limparEntrada(competencia, empresaId, funcionarios, feriados, dataPagamento)
    try {
      if (acao === 'simular') setResultado(await apiEtapa6.simularFolha(entrada))
      if (acao === 'gravar') {
        setResultado(await apiEtapa6.calcularFolha(entrada))
        setMsg({ erro: '', ok: 'Holerites gravados.' })
      }
      if (acao === 'lote') {
        const lote = await apiEtapa6.enfileirarFolha(entrada)
        setMsg({ erro: '', ok: `Lote ${lote.lote_id} enfileirado. O worker grava os holerites em segundo plano.` })
      }
    } catch (e) {
      setMsg({ erro: e.message, ok: '' })
    } finally {
      setCarregando(false)
    }
  }

  return (
    <section>
      <h2>Folha mensal</h2>
      <p className="fundamento">
        Folha integrada com a jornada da EC 141/2026 (42h a partir de 15/12/2026), afastamentos e licenças cadastrados e IRRF
        pela tabela da data de pagamento. Informe a data das horas extras quando o mês tiver mudança de jornada.
      </p>
      <div className="grade">
        <label>Competência<input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} /></label>
        <button type="button" className="secundario" onClick={importarDoPonto}>Importar do Ponto</button>
        <label>Data de pagamento<input type="date" value={dataPagamento} onChange={(e) => setDataPagamento(e.target.value)} placeholder="5º dia útil" /></label>
        <label>Feriados locais (AAAA-MM-DD, separados por vírgula)<input value={feriados} onChange={(e) => setFeriados(e.target.value)} placeholder="2026-10-28" /></label>
      </div>

      {funcionarios.map((f, i) => (
        <fieldset key={i}>
          <legend>Funcionário {i + 1}</legend>
          <div className="grade">
            <label>Matrícula<input value={f.funcionario_id} onChange={(e) => alterar(i, 'funcionario_id', e.target.value)} /></label>
            <label>Nome<input value={f.nome} onChange={(e) => alterar(i, 'nome', e.target.value)} /></label>
            <label>Salário base (mês cheio)<input type="number" step="0.01" min="0" value={f.salario_base} onChange={(e) => alterar(i, 'salario_base', e.target.value)} /></label>
            <label>Divisor (h/mês)<input type="number" step="0.01" min="1" value={f.jornada_mensal} placeholder="pela jornada" onChange={(e) => alterar(i, 'jornada_mensal', e.target.value)} /></label>
            <label>Dependentes IRRF<input type="number" min="0" value={f.dependentes_irrf} onChange={(e) => alterar(i, 'dependentes_irrf', e.target.value)} /></label>
          </div>
          {f.horas_extras.map((h, k) => (
            <div className="grade he" key={`he${k}`}>
              <label>Adicional HE (%)<input type="number" min="0" value={h.adicional_percentual} onChange={(e) => alterarItem(i, 'horas_extras', k, 'adicional_percentual', e.target.value)} /></label>
              <label>Horas<input type="number" step="0.01" min="0" value={h.horas} onChange={(e) => alterarItem(i, 'horas_extras', k, 'horas', e.target.value)} /></label>
              <label>Data<input type="date" value={h.data || ''} onChange={(e) => alterarItem(i, 'horas_extras', k, 'data', e.target.value)} /></label>
              <button type="button" className="link" onClick={() => alterar(i, 'horas_extras', f.horas_extras.filter((_, j) => j !== k))}>remover</button>
            </div>
          ))}
          {[...(f.horas_noturnas || []).map((h) => ['Adicional noturno', h]), ...(f.intervalo_suprimido || []).map((h) => ['Intervalo suprimido (indenizatório)', h])].map(([nome, h], k) => (
            <p className="fundamento" key={`pt${k}`}>Do Ponto: {nome} {h.adicional_percentual}% · {String(h.horas).replace('.', ',')} h{h.data ? ` até ${dataBR(h.data)}` : ''} (valor calculado pela Folha com o divisor da data)</p>
          ))}
          {f.outros_proventos.map((o, k) => (
            <div className="grade he" key={`op${k}`}>
              <label>Provento<input value={o.descricao} onChange={(e) => alterarItem(i, 'outros_proventos', k, 'descricao', e.target.value)} /></label>
              <label>Valor<input type="number" step="0.01" min="0" value={o.valor} onChange={(e) => alterarItem(i, 'outros_proventos', k, 'valor', e.target.value)} /></label>
              <label className="inline"><input type="checkbox" checked={o.gera_dsr} onChange={(e) => alterarItem(i, 'outros_proventos', k, 'gera_dsr', e.target.checked)} /> Reflete no DSR</label>
              <button type="button" className="link" onClick={() => alterar(i, 'outros_proventos', f.outros_proventos.filter((_, j) => j !== k))}>remover</button>
            </div>
          ))}
          <div className="acoes">
            <button type="button" className="secundario" onClick={() => alterar(i, 'horas_extras', [...f.horas_extras, { adicional_percentual: '50', horas: '', data: '' }])}>+ Hora extra</button>
            <button type="button" className="secundario" onClick={() => alterar(i, 'outros_proventos', [...f.outros_proventos, { descricao: 'Comissões', valor: '', gera_dsr: true, incide_inss: true, incide_irrf: true, incide_fgts: true }])}>+ Comissão ou variável</button>
            {funcionarios.length > 1 && <button type="button" className="link" onClick={() => setFuncionarios((fs) => fs.filter((_, j) => j !== i))}>Remover funcionário</button>}
          </div>
        </fieldset>
      ))}

      <div className="acoes">
        <button type="button" className="secundario" onClick={() => setFuncionarios((fs) => [...fs, novoFuncionario(fs.length + 1)])}>+ Funcionário</button>
        <button type="button" disabled={carregando || !empresaId} onClick={() => executar('simular')}>Simular</button>
        <button type="button" className="secundario" disabled={carregando || !empresaId} onClick={() => executar('gravar')}>Calcular e gravar</button>
        <button type="button" className="secundario" disabled={carregando || !empresaId} onClick={() => executar('lote')}>Processar em lote</button>
      </div>
      <Aviso>{msg.erro}</Aviso>
      <Aviso tipo="ok">{msg.ok}</Aviso>
      {doPonto && doPonto.funcionarios.some((p) => p.faltas_dias || Number(p.atrasos_horas) || p.dsr_perdidos || p.pendencias || Number(p.interjornada_suprimida_horas)) && (
        <div className="fundamento">
          <b>Do Ponto, para conferir e lançar como desconto:</b>
          <ul>{doPonto.funcionarios.filter((p) => p.faltas_dias || Number(p.atrasos_horas) || p.dsr_perdidos || p.pendencias || Number(p.interjornada_suprimida_horas)).map((p) => (
            <li key={p.funcionario_id}>{p.nome}: {[p.faltas_dias && `${p.faltas_dias} falta(s)`, p.atrasos_horas && `${String(p.atrasos_horas).replace('.', ',')} h de atraso`,
              p.dsr_perdidos && `${p.dsr_perdidos} DSR perdido(s)`, p.pendencias && `${p.pendencias} dia(s) com marcação pendente`,
              Number(p.interjornada_suprimida_horas) && `${String(p.interjornada_suprimida_horas).replace('.', ',')} h a menos entre jornadas (art. 66, pagar como extra se não houver acordo)`].filter(Boolean).join(' · ')}</li>))}</ul>
        </div>
      )}
      {resultado && <Resultado resultado={resultado} />}
    </section>
  )
}

function Resultado({ resultado }) {
  const t = resultado.totais
  const extras = Object.keys(t).filter((k) => NOMES_ENCARGOS[k])
  return (
    <>
      <Cards itens={[
        ['Proventos', moeda(t.proventos)],
        ['Descontos', moeda(t.descontos)],
        ['Líquido', moeda(t.liquido)],
        ['Encargos patronais', moeda(t.encargos_patronais), extras.map((k) => `${NOMES_ENCARGOS[k]} ${moeda(t[k])}`).join(' · ')],
        ['Salário-família a deduzir', moeda(t.salario_familia_a_deduzir), 'Compensado nas contribuições'],
        ['Licenças a compensar', moeda(t.licencas_a_compensar), 'Salário-maternidade e salário-paternidade'],
      ]} />
      {resultado.avisos?.length > 0 && <div className="aviso"><ul>{resultado.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul></div>}
      <p className="fundamento">
        Parâmetros {resultado.parametros_versao} · {resultado.dias_uteis} dias úteis · {resultado.domingos_feriados} repousos e feriados ·
        {' '}pagamento em {dataBR(resultado.data_pagamento)} (IRRF da competência {resultado.competencia_irrf})
      </p>
      {resultado.holerites.map((h) => (
        <div className="holerite" key={h.funcionario_id}>
          <h3>{h.nome || h.funcionario_id}</h3>
          {h.afastamentos_no_mes.length > 0 && (
            <p className="fundamento">
              Afastamentos: {h.afastamentos_no_mes.map((a) => `${a.descricao} (empresa ${a.dias_pagos_empresa}d · compensado ${a.dias_beneficio_compensado}d · INSS ${a.dias_inss}d · sem remuneração ${a.dias_sem_remuneracao}d)`).join('; ')}
            </p>
          )}
          {h.ferias_no_mes.length > 0 && (
            <p className="fundamento">
              Férias no mês: {h.ferias_no_mes.map((f) => `${dataBR(f.inicio_gozo)} a ${dataBR(f.fim_gozo)} (${f.dias_no_mes} dias)`).join('; ')} ·
              {' '}salário de contribuição total {moeda(h.base_inss_consolidada)} · INSS do mês {moeda(h.inss_consolidado)}
            </p>
          )}
          <table>
            <thead><tr><th>Cód.</th><th>Descrição</th><th>Ref.</th><th className="num">Proventos</th><th className="num">Descontos</th></tr></thead>
            <tbody>
              {h.rubricas.map((r, i) => (
                <tr key={i} className={r.tipo === 'INFORMATIVO' ? 'informativo' : ''}>
                  <td>{r.codigo}</td><td>{r.descricao}</td><td>{r.referencia}</td>
                  <td className="num">{r.tipo === 'PROVENTO' ? moeda(r.valor) : r.tipo === 'INFORMATIVO' ? `(${moeda(r.valor)})` : ''}</td>
                  <td className="num">{r.tipo === 'DESCONTO' ? moeda(r.valor) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan="3">Totais</td><td className="num">{moeda(h.total_proventos)}</td><td className="num">{moeda(h.total_descontos)}</td></tr>
              <tr><td colSpan="3"><strong>Líquido a receber</strong></td><td colSpan="2" className="num"><strong>{moeda(h.liquido)}</strong></td></tr>
            </tfoot>
          </table>
          <p className="fundamento">
            {h.dias_trabalhados} dias de salário · Base INSS {moeda(h.base_inss)} · Base patronal {moeda(h.base_patronal)} · Base IRRF {moeda(h.base_irrf)} · Base FGTS {moeda(h.base_fgts)}
            <br />Divisor: {h.divisores.join(' e ')} · Tabela IRRF {h.tabela_irrf_versao}
          </p>
          <details>
            <summary>Encargos patronais e memória de cálculo</summary>
            <ul>{h.encargos_patronais.map((e) => <li key={e.encargo}>{NOMES_ENCARGOS[e.encargo] || e.encargo}: {pct(e.aliquota, 4)} × {moeda(e.base)} = {moeda(e.valor)}</li>)}</ul>
            <ul className="memoria">{h.memoria_calculo.map((m, i) => <li key={i}>{m}</li>)}</ul>
          </details>
        </div>
      ))}
    </>
  )
}
